/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { restartableTask } from 'ember-concurrency';
import { CollectionViewResourceName, viewIsLocked } from 'volta/models/collection-view';
import { normalize } from 'volta/utils/api/serialize-and-push';
import { distinct, indexByFn } from 'volta/utils/array-utils';
import { defaultErrorHandler } from 'volta/utils/error-utils';
import { niFilter } from 'volta/utils/filters-utils';

import { action } from '@ember/object';
import { guidFor } from '@ember/object/internals';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import type { BufferedChangeset } from 'ember-changeset/types';
import type CollectionView from 'volta/models/collection-view';
import type User from 'volta/models/user';
import type UserProfile from 'volta/models/user-profile';
import type UserRole from 'volta/models/user-role';
import type { IJsonApiQuery } from 'volta/utils/api/jsonapi-types';
import type { ICollectionArgs, ICollectionGroupOption } from 'volta/models/types/collection-view';
import type { TError } from 'volta/utils/error-utils';

import type { IntlService } from 'ember-intl';
import type StoreService from 'volta/services/store';
import type CustomFieldDefinitionsService from 'volta/services/custom-field-definitions-service';
import type SessionUserService from 'volta/services/session-user';
import type {
  ICollectionFilter,
  ICollectionProperty,
  ICollectionViewDefinition,
  ICollectionViewEditCmd,
  TCollectionPropertyForUI,
  TCollectionType,
  TCreationMode
} from 'volta/models/types/collection-view';
import type Owner from '@ember/owner';

interface CollectionViewSettingsSignature {
  Args: {
    isCreateMode: boolean;
    view?: CollectionView;
    filterDefinitions: TFilterDefinition[];
    collectionDefinition: ICollectionViewDefinition;
    settingsSection: string;
    metadata?: object;
    groupByOptions: ICollectionGroupOption[];
    enableGrouping?: boolean;
    onFilterChange?: (value: TFilterValue, field: string) => void;
    onPropertyChange?: (props: ICollectionProperty[]) => void;
    onGroupByChange?: (group: ICollectionGroupOption, checked: boolean) => void;
    onComponentArgsChange?: (settings: ICollectionArgs) => void;
    onGroupByOptionsReorder?: (groupBy: ICollectionGroupOption[]) => void;
    onCreate?: () => void;
    onEdit?: () => void;
    onClose: () => void;
    onDelete?: () => void;
    changeset: BufferedChangeset & ICollectionViewEditCmd;
  };
  Element: HTMLElement;
}

export default class CollectionViewSettingsComponent extends Component<CollectionViewSettingsSignature> {
  @service intl!: IntlService;
  @service store!: StoreService;
  @service sessionUser!: SessionUserService;
  @service customFieldDefinitionsService!: CustomFieldDefinitionsService;

  @tracked creationMode?: TCreationMode;
  @tracked changeset!: BufferedChangeset;
  @tracked templates: Partial<CollectionView>[] = [this.args.collectionDefinition.defaultView];
  @tracked selectedTemplateId?: string | 'default';
  // eslint-disable-next-line ember/no-tracked-properties-from-args
  @tracked settingsSection: string | undefined = this.args.settingsSection;
  @tracked selectedUsers: UserProfile[] = [];
  @tracked usersList: UserProfile[] = this.args.view?.showForUsers?.slice() ?? [];
  @tracked lockedIsChecked = false;
  @tracked lockedIsDirty = false;

  resourceName = CollectionViewResourceName;
  elementId = guidFor(this);

  get lockedError() {
    if (!this.lockedIsChecked || this.args.isCreateMode) {
      return undefined;
    }

    return (this.cs.get('showForRoles') ?? []).length > 0 ||
      (this.cs.get('showForUsers') ?? []).length > 0
      ? undefined
      : this.intl.t('collectionViewsPage.errorDefaultTemplate');
  }

  get cs() {
    return this.args.changeset;
  }

  get selectedTemplate() {
    return this.templates.find(
      (template) => (template.id ?? 'default') === this.selectedTemplateId
    );
  }

  get isLocked() {
    if (this.args.isCreateMode || !this.args.view) {
      return false;
    }
    return viewIsLocked(this.args.view, this.sessionUser.userId);
  }

  get showViewPanel() {
    return Boolean(this.args.collectionDefinition.argsComponent);
  }

  get typeBlocks() {
    type TTypeBlock = { value: TCollectionType; title: string; icon: string };
    return (
      [
        {
          value: 'TABLE',
          title: this.intl.t('collectionViewsPage.types.table'),
          icon: 'table-view'
        },
        {
          value: 'KANBAN',
          title: this.intl.t('collectionViewsPage.types.kanban'),
          icon: 'kanban-view'
        },
        {
          value: 'TIMELINE',
          title: this.intl.t('collectionViewsPage.types.timeline'),
          icon: 'timeline-view'
        },
        { value: 'LIST', title: this.intl.t('collectionViewsPage.types.list'), icon: 'list-view' }
      ] as TTypeBlock[]
    ).filter((type: TTypeBlock) => this.args.collectionDefinition.types.includes(type.value));
  }

  @cached
  get indexedDefinitions() {
    return indexByFn(this.args.collectionDefinition.definitions, (d) => d.property ?? d.valuePath);
  }

  @cached
  get indexedCurrentProperties() {
    return indexByFn(
      this.args.changeset.get('properties') as ICollectionProperty[],
      ({ property }) => property
    );
  }

  @cached
  get properties() {
    const props = this.indexedCurrentProperties;
    const defs = this.indexedDefinitions;
    const properties: TCollectionPropertyForUI[] = [];

    Object.values(props).forEach((p) => {
      const def = defs[p.property];
      if (def) {
        properties.push({ ...p, color: def.color, disabled: false, isStatic: def.isStatic });
      }
    });

    Object.values(defs).forEach((d) => {
      const notPresent = Boolean(!props[d.property ?? d.valuePath]);
      if (this.args.isCreateMode || notPresent) {
        properties.push({
          property: d.property ?? d.valuePath,
          title: d.columnName,
          isCustomField: d.isCustomField ?? false,
          disabled: this.args.isCreateMode ? false : notPresent,
          isFixed: d.isFixed,
          isGroupable: d.isGroupable,
          color: d.color
        });
      }
    });

    return properties;
  }

  constructor(owner: Owner, args: CollectionViewSettingsSignature['Args']) {
    super(owner, args);
    const { view } = this.args;
    this.lockedIsChecked = view
      ? ((view.showForUsers ?? []).length > 0 || (view.showForRoles ?? []).length > 0) &&
        !view.isTemplate
      : false;
  }

  @action
  selectTemplate(selectedTemplate: CollectionView) {
    this.selectedTemplateId = selectedTemplate.id ?? 'default';
    const template: ICollectionViewEditCmd | undefined = {
      ...selectedTemplate.toCreateCmd(),
      ...selectedTemplate.toShareCmd()
    };
    Object.entries(template).forEach(([key, value]) => {
      this.args.changeset.set(key, value);
    });
  }

  @action
  toggleAccordeon(id: string, checked: boolean) {
    this.settingsSection = checked ? id : undefined;
  }

  @action
  changeCreationMode(mode: unknown) {
    this.creationMode = mode as TCreationMode;
    if (mode === 'manual') {
      this.args.changeset.rollback();
    }
  }

  @action
  decorateUserQuery(query: IJsonApiQuery) {
    const { filter = {} } = query;
    filter['userType'] = niFilter(['SUPPLIER', 'API']);
    query.filter = filter;
    return query;
  }

  @action
  setUser(userId: string, [_user]: [User]) {
    this.args.changeset.set(
      'showForUsers',
      distinct([...(this.args.changeset.get('showForUsers') ?? []), userId]).flat()
    );
  }

  @action
  removeUsers() {
    const users = indexByFn(this.selectedUsers, (u) => u.id!);
    this.usersList = this.usersList.filter((u) => u.id && !users[u.id]);
    this.args.changeset.set(
      'showForUsers',
      this.usersList.map((u) => u.id)
    );
    this.selectedUsers = [];
  }

  @action
  changeShowForRoles(ids: string[], _roles: UserRole[]) {
    this.args.changeset.set('showForRoles', ids);
    this.args.changeset.set('isTemplate', !this.lockedIsChecked);
  }

  @action
  changeShowForUsers(ids: string[], users: UserProfile[]) {
    this.usersList = users;
    this.args.changeset.set('showForUsers', ids);
    this.args.changeset.set('isTemplate', !this.lockedIsChecked);
  }

  @action
  changeLocked(checked: boolean) {
    this.lockedIsChecked = checked;
    this.lockedIsDirty = true;
    this.args.changeset.set('isTemplate', !this.lockedIsChecked);
  }

  @action
  onCreate() {
    if (this.creationMode === 'template') {
      this.args.changeset.set(
        'name',
        this.args.changeset.get('name') + ` (${this.intl.t('copySingular')})`
      );
    }

    this.args.onCreate?.();

    if (this.args.changeset.isValid) {
      this.args.onClose();
    }
  }

  @action
  onEdit() {
    this.args.onEdit?.();
    this.args.onClose();
  }

  @action
  scrollToSection(section: string = 'general') {
    const elementToScrollTo = document.getElementById(this.sectionId(section));

    if (elementToScrollTo) {
      elementToScrollTo.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest'
      });
    }
  }

  // Tasks

  fetchTemplates = restartableTask(async () => {
    try {
      const defaultView = this.store.createRecord<CollectionView>('collection-view', {
        ...this.args.collectionDefinition.defaultView
      });
      const data = await this.store.rawQuery(
        'collection-view',
        ['mine', defaultView.entityType as string, 'templates'],
        { include: 'showForUsers,showForRoles' }
      );
      const templates = normalize('collection-view', data) as unknown as CollectionView[];
      this.templates = [defaultView, ...templates];
      this.selectTemplate(defaultView);
      return templates;
    } catch (error) {
      defaultErrorHandler(error as TError);
      return;
    }
  });

  // helpers
  initialValue(filters: ICollectionFilter[] = [], queryParam: string) {
    return filters.find((filter: ICollectionFilter) => filter.property === queryParam);
  }

  sectionId = (id: string) => `${this.elementId}${id}`;
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'CollectionView::Settings': typeof CollectionViewSettingsComponent;
    'collection-view/settings': typeof CollectionViewSettingsComponent;
  }
}
